import { FormEvent, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useOperatorConfig } from '../hooks/useOperatorConfig';
import logoUrl from '../assets/ARES_Logo.png';

export default function LoginPage() {
  const { login, isLoading } = useAuth();
  const { config } = useOperatorConfig();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const backendSummary = useMemo(() => {
    const target = config.backendUrl.trim();
    if (!target) {
      if (typeof window === 'undefined') {
        return 'Using configured origin';
      }
      return 'Using current browser origin';
    }
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : undefined;
      const url = base ? new URL(target, base) : new URL(target);
      return url.origin;
    } catch (error) {
      return target;
    }
  }, [config.backendUrl]);

  const livekitSummary = useMemo(() => {
    const target = config.livekitUrl.trim();
    if (!target) {
      return 'Not configured';
    }
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : undefined;
      const url = base ? new URL(target, base) : new URL(target);
      return url.origin;
    } catch (error) {
      return target;
    }
  }, [config.livekitUrl]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setStatus('Signing in…');

    const result = await login(username.trim(), password);

    if (!result.success) {
      setStatus(null);
      setError('message' in result ? result.message : 'Unable to sign in.');
      return;
    }

    setStatus('Authenticated');
    setPassword('');
  };

  return (
    <div className="login-shell">
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="login-header" style={{ alignItems: 'center' }}>
          <img
            src={logoUrl}
            alt="A.R.E.S logo"
            style={{
              width: '180px',
              maxWidth: '65%',
              height: 'auto',
              objectFit: 'contain',
              filter: 'drop-shadow(0 12px 24px rgba(15, 23, 42, 0.65))',
            }}
          />
          <p style={{ margin: '0.85rem 0 0', color: '#94a3b8', fontSize: '0.95rem' }}>
            Operator Console · Sign in with your administrator-provisioned credentials.
          </p>
        </div>
        <div className="login-field">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            name="username"
            type="text"
            value={username}
            onChange={(event) => {
              setUsername(event.target.value);
              setError(null);
              setStatus(null);
            }}
            autoComplete="username"
            placeholder="Enter username"
            required
            disabled={isLoading}
          />
        </div>
        <div className="login-field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setError(null);
              setStatus(null);
            }}
            autoComplete="current-password"
            placeholder="Enter password"
            required
            disabled={isLoading}
          />
        </div>
        {status ? (
          <div
            style={{
              padding: '0.75rem 1rem',
              borderRadius: '12px',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              background: 'rgba(37, 99, 235, 0.15)',
              color: '#bfdbfe',
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          >
            {status}
          </div>
        ) : null}
        {error ? <div className="login-error" role="alert">{error}</div> : null}
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="login-footnote">
          Passwords are stored server-side using PBKDF2-SHA256 with per-user salts and high iteration counts.
        </p>
      </form>
      <aside className="login-config-indicator">
        <span className="label">Environment</span>
        <div>
          <span className="key">API:</span> {backendSummary}
        </div>
        <div>
          <span className="key">LiveKit:</span> {livekitSummary}
        </div>
      </aside>
    </div>
  );
}
